import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import styled from 'styled-components';
import { StandardButton } from './GlobalElements/StandardButton.js';
import { tokens } from "../../public/tokens.js";

const { colors, typography, globals } = tokens;

const SignatureContainer = styled.div`
  max-width: 28rem;
  width: 100%;
  position: relative;
`;

const CanvasWrapper = styled.div`
  border: 1px solid ${colors.surfaces['border-divider']};
  border-radius: ${globals['border-radius']};
  overflow: hidden;
  position: relative;
  padding-top: 20px;
`;

const StyledCanvas = styled.canvas`
  position: relative;
  left: 20px;
  cursor: ${props => {
    if (props.disabled) return 'not-allowed';
    if (props.tool === 'pencil') return 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cline x1=\'18\' y1=\'2\' x2=\'22\' y2=\'6\'%3E%3C/line%3E%3Cpath d=\'M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
    if (props.tool === 'eraser') return 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21\'%3E%3C/path%3E%3Cpath d=\'M22 21H7\'%3E%3C/path%3E%3Cpath d=\'m5 11 9 9\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
    return 'crosshair';
  }};
`;

const Divider = styled.hr`
  margin-top: 0;
  border: 0;
  border-top: 1px solid ${colors.surfaces['border-divider']};
`;

const SignHereText = styled.div`
  text-align: center;
  position: relative;
  left: 0;
  right: 0;
  color: ${colors.text.secondary};
  margin: 0.5rem 0;
`;

const Toolbar = styled.div`
  position: absolute;
  background: ${colors.neutral.white};
  padding: 0.25rem;
  display: flex;
  gap: 10px;
  top: 10px;
  right: 10px;
  
  @media (min-width: 576px) {
    padding: 0;
  }
`;

const ToolIcon = styled.i`
  cursor: pointer;
  color: ${colors.text.primary};
  font-size: ${typography.fontSize['lg-14px']}px;
  display: ${props => props.visible ? 'flex' : 'none'};
  
  &:hover {
    color: ${tokens.colors.theme['highlight-color']};
  }
  
  ${props => props.disabled && `
    cursor: not-allowed;
    opacity: 0.5;
    
    &:hover {
      color: ${colors.text.primary};
    }
  `}
`;

const ButtonGroup = styled.div`
  display: ${props => props.visible ? 'flex' : 'none'};
  margin: ${globals['spacing-sm']} 0;
  flex-direction: column;
  gap: 8px;
  
  @media (min-width: 576px) {
    flex-direction: row;
  }
`;

const SignaturePadComponent = ({
  signaturePadRef, 
  formik, 
  setShowIp, 
  showIp,
  setIsSig,
  readOnly,
  isCancel,
  from,
  loading,
  readOnlyData
}) => {
  const canvasRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [erasorDisplay, setErasorDisplay] = useState(true);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [tool, setTool] = useState('pencil');
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const signaturePad = new SignaturePad(canvas, {
      backgroundColor: colors.neutral.white,
      penColor: colors.neutral.black
    });
    signaturePadRef.current = signaturePad;
    
    const checkEmpty = () => {
      const currentData = signaturePad.toData();
      setUndoStack(currentData);
      setRedoStack([]);
      setIsEmpty(signaturePad.isEmpty());
    };
    
    signaturePad.addEventListener('endStroke', checkEmpty);
    
    return () => {
      signaturePad.removeEventListener('endStroke', checkEmpty);
      signaturePad.clear();
    };
  }, []);

  useEffect(() => {
    if (signaturePadRef.current) {
      if (isDisabled) {
        signaturePadRef.current.off();
        const canvas = canvasRef.current;
        const overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '40px';
        overlay.style.bottom = '40px';
        overlay.style.cursor = 'not-allowed';
        overlay.style.zIndex = '1000';
        overlay.className = 'signature-overlay';
        canvas.parentElement.style.position = 'relative';
        canvas.parentElement.appendChild(overlay);
        
        return () => {
          const existingOverlay = canvas.parentElement.querySelector('.signature-overlay');
          if (existingOverlay) {
            canvas.parentElement.removeChild(existingOverlay);
          }
        };
      } else {
        signaturePadRef.current.on();
        const canvas = canvasRef.current;
        const existingOverlay = canvas.parentElement.querySelector('.signature-overlay');
        if (existingOverlay) {
          canvas.parentElement.removeChild(existingOverlay);
        }
      }
    }
  }, [isDisabled]);

  useEffect(() => {
    if (isCancel) {
      handleCancel();
    }
    },[isCancel])

    // Convert base64 to Blob with specific image type
    const base64ToFile = (dataUrl) => {
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        
        // Create a File object instead of a Blob
        return new File([u8arr], `signature.${mime.split('/')[1]}`, { type: mime });
    };
    
  const saveSignature = () => {
    if (signaturePadRef.current.isEmpty()) {
      // alert("Please provide a signature first.");
    } else {
      const dataURL = signaturePadRef.current.toDataURL('image/png', 1.0);
      if (from == 'quiz') {
        localStorage.setItem('localImage', JSON.stringify(dataURL));
      }
      const binary = base64ToFile(dataURL);
      setIsSig(binary);
      setShowIp(true);
      setIsDisabled(true);
      formik && formik.setFieldValue('student_signature', binary);
    }
  };

  const handleCancel = () => {
    setShowIp(false);
    setIsDisabled(false);
    formik && formik.setFieldValue('student_signature', null);
    setIsSig(null);
    signaturePadRef.current.clear();
    setIsEmpty(true);
    signaturePadRef.current.penColor = colors.neutral.black;
    setErasorDisplay(true);
    setTool('pencil');
    setUndoStack([]);
    setRedoStack([]);   
  };

  const erase = () => {
    signaturePadRef.current.penColor = colors.neutral.white;
    setErasorDisplay(false);
    setTool('eraser');
  };

  const draw = () => {
    signaturePadRef.current.penColor = colors.neutral.black;
    setErasorDisplay(true);
    setTool('pencil');
  };

  const undo = () => {
    setShowIp(false);
    const data = signaturePadRef.current.toData();
    if (data.length > 0) {
      const lastStroke = data[data.length - 1];
      const newData = data.slice(0, -1);
      
      signaturePadRef.current.clear();
      signaturePadRef.current.fromData(newData);
      
      setUndoStack(newData);
      setRedoStack(prevStack => [...prevStack, lastStroke]);
      setIsEmpty(signaturePadRef.current.isEmpty());
      
      if (signaturePadRef.current?.isEmpty()) {
        signaturePadRef.current.penColor = colors.neutral.black;
        setErasorDisplay(true);
        setTool('pencil');
      }
    }
  };

  const redo = () => {
    if (redoStack.length > 0) {
      const strokeToRedo = redoStack[redoStack.length - 1];
      const currentData = signaturePadRef.current.toData();
      const newData = [...currentData, strokeToRedo];
      
      signaturePadRef.current.clear();
      signaturePadRef.current.fromData(newData);
      
      setUndoStack(newData);
      setRedoStack(prevStack => prevStack.slice(0, -1));
      setIsEmpty(false);
    }
  };
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (isDisabled) {
      canvas.style.cursor = 'not-allowed';
    } else if (tool === 'pencil') {
      canvas.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cline x1=\'18\' y1=\'2\' x2=\'22\' y2=\'6\'%3E%3C/line%3E%3Cpath d=\'M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
    } else if (tool === 'eraser') {
      canvas.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21\'%3E%3C/path%3E%3Cpath d=\'M22 21H7\'%3E%3C/path%3E%3Cpath d=\'m5 11 9 9\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
    }
  }, [tool, isDisabled]);

  const showButtonGroup = readOnly ? from == 'quiz' : true;

  return (
    <SignatureContainer>
      <CanvasWrapper>
        <StyledCanvas ref={canvasRef} disabled={isDisabled} tool={tool} />
        <Divider />
        <SignHereText>Sign Above</SignHereText>
        <Toolbar>
          <ToolIcon role="button" onClick={undo} visible={!showIp} className="fal fa-undo" />
          <ToolIcon role="button" onClick={redo} visible={!showIp} className="fal fa-redo" />
          {erasorDisplay ? (
            <ToolIcon
              role="button"
              onClick={() => (isEmpty || loading ? null : erase())}
              disabled={isEmpty || loading}
              visible={!showIp}
              className="fal fa-eraser"
            />
          ) : (
            <ToolIcon role="button" onClick={draw} visible={!showIp} className="fal fa-pencil" />
          )}
        </Toolbar>
      </CanvasWrapper>

      <ButtonGroup visible={showButtonGroup}>
        {!showIp ? (
          <StandardButton disabled={isEmpty} onClick={saveSignature}>
            Save Signature
          </StandardButton>
        ) : (
          <StandardButton variant="danger" disabled={loading} onClick={handleCancel}>
            Remove Signature
          </StandardButton>
        )}
      </ButtonGroup>
    </SignatureContainer>
  )
};

export default SignaturePadComponent;